// Public tenant branding for the pre-session sign-in surface (E1 US-1.1).
//
// The sign-in page has to answer three questions before anyone can authenticate: whose organisation is
// this (name + logo), is the external-learner email-link entry point offered here at all, and WHERE do
// this tenant's people authenticate. All three come from one unauthenticated read against the CLMS-owned
// auth surface:
//
//   GET /api/auth/tenant  -> 200 { name, logoUrl, allowsExternalLearners, idpAlias }
//                          · 404 unknown tenant  · 429 rate-limited
//
// The tenant travels as the `X-Tenant` hint, stated by the caller rather than derived — the lookup is
// asked about a slug and must ask the backend about that one (see fetchTenantBranding). The four fields
// are the whole of what the backend
// discloses — there is no id, no slug and no listing — so this module cannot leak more than the sign-in
// page shows (backend/src/Clms.Api/Security/AuthEndpoints.cs).

import { useEffect, useState } from "react";
import type { components } from "@clms/contracts";
import { apiFetch, ApiError } from "../lib/api";
import { resolveTenantSlug } from "../config";

type TenantBrandingResponse = components["schemas"]["TenantBrandingResponse"];

/** The tenant's public sign-in identity. */
export interface TenantBranding {
  name: string;
  logoUrl: string | null;
  allowsExternalLearners: boolean;
  /**
   * The realm IdP alias to send as `kc_idp_hint`, so this tenant's people land on their own identity
   * provider instead of the realm's generic login page (ADR-0014 §12 C-6) — null when they authenticate
   * with the platform realm's own credentials, which is every tenant without a federation.
   *
   * **Taken from the backend, never built here.** The alias is `tenant-<slug>` and the SPA holds the slug,
   * so composing it locally would compile and work — and would be the third copy of a derivation that
   * `TenantIdpFederation.AliasFor` exists to be the only one of (the console and the provisioning
   * component are the other two ends). It is also not ours to decide: the backend answers the alias only
   * while the arrangement is in force, and a locally built one would point at a broker the realm may not
   * carry yet (`TenantBrandingLookup.IdpHintFor`).
   */
  idpAlias: string | null;
}

/**
 * What the sign-in surface knows about its tenant right now.
 * - `none`    — no tenant is addressable in the URL (bare apex host in local dev); the platform brand
 *               stands in and nothing tenant-specific is claimed.
 * - `loading` — the lookup is in flight.
 * - `ready`   — the tenant answered; `branding` is what to draw.
 * - `unknown` — 404: no such organisation. The view's "Organisaatiota ei löytynyt" state (US-1.1).
 * - `error`   — the lookup itself failed (offline, 5xx, 429). Distinct from `unknown`: an organisation
 *               that could not be reached must not be reported to the learner as non-existent.
 */
export type TenantBrandingState =
  | { status: "none" }
  | { status: "loading" }
  | { status: "ready"; branding: TenantBranding }
  | { status: "unknown" }
  | { status: "error" };

/** A state the lookup can settle on (everything except the two states it starts in). */
type SettledState = Extract<TenantBrandingState, { status: "ready" | "unknown" | "error" }>;

/**
 * One in-flight/settled lookup per slug for the lifetime of the document. Both the layout (logo + name)
 * and the sign-in view (the external-learner link) ask the same question on the same render, and a
 * second HTTP call to answer it twice would be the only visible effect. A full page load clears it,
 * which is the refresh path; `error` is evicted on settle so a remount retries rather than caching a
 * blip forever.
 */
const inFlight = new Map<string, Promise<SettledState>>();

/** Read one tenant's public branding. Never throws — every outcome is a state. */
export function fetchTenantBranding(slug: string): Promise<SettledState> {
  const cached = inFlight.get(slug);
  if (cached) return cached;

  const pending = apiFetch<TenantBrandingResponse>("/api/auth/tenant", {
    // THE SLUG THIS LOOKUP WAS ASKED ABOUT, stated rather than left to `apiFetch`'s own derivation from
    // the URL. The two agree on the sign-in page, which is where this read was born — and they do NOT
    // agree on the other caller: `fetchIdpHint` is also used by a step-up, which is taken from an in-app
    // route, and every one of those is apex-only (`app/router.tsx`), so the derived hint is empty there
    // and the backend would be asked about no tenant at all. Passing it also keeps the memo key and the
    // request the same question, which `inFlight` already assumes they are.
    headers: { "X-Tenant": slug },
  })
    .then<SettledState>((body) => ({
      status: "ready",
      branding: {
        name: body.name,
        logoUrl: body.logoUrl ?? null,
        allowsExternalLearners: body.allowsExternalLearners,
        idpAlias: body.idpAlias ?? null,
      },
    }))
    .catch<SettledState>((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) return { status: "unknown" };
      inFlight.delete(slug); // transient — let the next mount try again
      return { status: "error" };
    });

  inFlight.set(slug, pending);
  return pending;
}

/**
 * The `kc_idp_hint` to put on an authorization request for the tenant `slug` addresses, or null for none
 * (ADR-0014 §12 C-6). The ONE resolver every sign-in leg calls — `LoginPage`, `PlatformLoginPage` and
 * `auth/stepUp` — so that what rides to Keycloak is decided here and not three times.
 *
 * It AWAITS the lookup rather than reading the render's state, and that is the point rather than
 * convenience: the lookup is in flight while the page draws, so a person who clicks the sign-in button
 * promptly would otherwise be redirected with no hint and meet the realm's generic login page, where their
 * own provider is deliberately not offered (`hideOnLogin`) — a federated tenant's sign-in would fail for
 * being fast. Awaiting costs nothing, because the promise is the SAME memoized one the page is already
 * waiting on: no second request, and by click time it has almost always settled.
 *
 * Null for every outcome that is not a tenant that answered: no tenant in the URL (the operator console,
 * and the bare apex host in dev), an unknown organisation, and a lookup that failed. The last is the
 * deliberate one — a federated tenant whose branding read fell over signs in through the realm's own page
 * instead of being redirected nowhere, which is the worse half of a sign-in to lose.
 */
export async function fetchIdpHint(slug: string | null): Promise<string | null> {
  if (!slug) return null;
  const settled = await fetchTenantBranding(slug);
  return settled.status === "ready" ? settled.branding.idpAlias : null;
}

/** Test seam: drop every memoized lookup (the SPA itself never needs this — a reload does it). */
export function resetTenantBrandingCache(): void {
  inFlight.clear();
}

/**
 * The branding of the tenant the current URL addresses. Safe to call from more than one component on a
 * page: the lookup is shared, so the layout and the view it wraps cost one request between them.
 */
export function useTenantBranding(): TenantBrandingState {
  const slug = resolveTenantSlug();
  const [state, setState] = useState<TenantBrandingState>(
    slug ? { status: "loading" } : { status: "none" },
  );

  useEffect(() => {
    if (!slug) {
      setState({ status: "none" });
      return;
    }
    let active = true;
    setState({ status: "loading" });
    void fetchTenantBranding(slug).then((settled) => {
      if (active) setState(settled);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  return state;
}

/** First letter of a name, for the fallback mark a tenant without a logo gets. */
export function monogramOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "V";
}
